import React, { useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Square, Sparkles, Wrench, Table2 } from "lucide-react";
import { ModeSelectorPill, ResearchDepthToggle, type AssistantMode } from "./ModeSelector";
import { modeConfig } from "./modeConfig";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (e?: React.FormEvent) => void;
  onStop: () => void;
  isLoading: boolean;
  mode: AssistantMode;
  onModeChange: (mode: AssistantMode) => void;
  placeholder?: string | undefined;
  /** Compact mode for side panel */
  compact?: boolean | undefined;
  /** Whether deep research is enabled (for panel mode Ask tab) */
  isDeepResearch?: boolean | undefined;
  /** Callback to toggle deep research (for panel mode Ask tab) */
  onDepthToggle?: ((isDeep: boolean) => void) | undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Loading Indicator
// ─────────────────────────────────────────────────────────────────────────────

const ThinkingIndicator: React.FC<{ mode: AssistantMode; compact?: boolean }> = ({ mode, compact }) => {
  const config = modeConfig[mode];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`flex items-center gap-2 ${compact ? "px-2 py-1" : "px-3 py-2"}`}
    >
      <div className="flex items-center gap-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className={`h-1.5 w-1.5 rounded-full bg-${config.color}-500`}
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 0.8,
              repeat: Infinity,
              delay: i * 0.15,
            }}
          />
        ))}
      </div>
      <span className={`text-xs text-${config.color}-400`}>
        {mode === "deep" ? "Researching..." : mode === "workspace" ? "Building..." : mode === "sheets" ? "Designing..." : "Thinking..."}
      </span>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const ChatComposer: React.FC<ChatComposerProps> = ({
  value,
  onChange,
  onSubmit,
  onStop,
  isLoading,
  mode,
  onModeChange,
  placeholder,
  compact = false,
  isDeepResearch,
  onDepthToggle,
}) => {
  // Show depth toggle when in Ask tab (quick/deep modes) and toggle handler is provided
  const showDepthToggle = onDepthToggle && mode !== "workspace" && mode !== "sheets";
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const defaultPlaceholder =
    mode === "deep"
      ? "Ask for deep research..."
      : mode === "workspace"
      ? "Describe widgets to create..."
      : mode === "sheets"
      ? "Describe a spreadsheet to create..."
      : "Ask a quick question...";

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, compact ? 100 : 200)}px`;
    }
  }, [value, compact]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  // Get submit button color based on mode
  const getSubmitButtonClass = () => {
    if (value.trim().length === 0) {
      return "bg-gray-800 text-gray-500 cursor-not-allowed";
    }
    switch (mode) {
      case "deep":
        return "bg-violet-500 hover:bg-violet-400 text-white shadow-lg shadow-violet-500/25";
      case "workspace":
        return "bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg shadow-emerald-500/25";
      case "sheets":
        return "bg-cyan-500 hover:bg-cyan-400 text-white shadow-lg shadow-cyan-500/25";
      default:
        return "bg-amber-500 hover:bg-amber-400 text-white shadow-lg shadow-amber-500/25";
    }
  };

  // Get submit icon based on mode
  const getSubmitIcon = () => {
    switch (mode) {
      case "deep":
        return Sparkles;
      case "workspace":
        return Wrench;
      case "sheets":
        return Table2;
      default:
        return Send;
    }
  };

  const SubmitIcon = getSubmitIcon();

  return (
    <div className={`flex-shrink-0 bg-gray-900 ${compact ? "px-2 py-2" : "pt-4 pb-6 px-3 md:px-4"}`}>
      <form onSubmit={handleSubmit} className={compact ? "" : "mx-auto max-w-4xl"}>
        {/* Loading state */}
        <AnimatePresence>
          {isLoading && <ThinkingIndicator mode={mode} compact={compact} />}
        </AnimatePresence>

        {/* Main Input Container */}
        <div className={`rounded-xl bg-gray-800/60 border border-gray-700/50 ${compact ? "" : "rounded-2xl"}`}>
          {/* Top Row: Mode selector or depth toggle */}
          <div className={`flex items-center justify-between border-b border-gray-700/30 ${compact ? "px-2 py-1.5" : "px-3 py-2"}`}>
            {showDepthToggle ? (
              <ResearchDepthToggle
                isDeep={isDeepResearch ?? false}
                onToggle={onDepthToggle}
                disabled={isLoading}
              />
            ) : (
              <ModeSelectorPill mode={mode} onModeChange={onModeChange} disabled={isLoading} />
            )}
            {!compact && (
              <div className="hidden sm:flex items-center gap-1 text-[10px] text-gray-500">
                <kbd className="px-1.5 py-0.5 rounded bg-gray-800 border border-gray-700 font-mono">⏎</kbd>
                <span>send</span>
              </div>
            )}
          </div>

          {/* Input Row */}
          <div className={`flex items-end gap-2 ${compact ? "p-2" : "p-3"}`}>
            {/* Textarea */}
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                rows={1}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder || defaultPlaceholder}
                className={`w-full resize-none bg-transparent text-white placeholder:text-gray-500 focus:outline-none leading-relaxed ${
                  compact ? "text-sm py-1" : "text-sm py-2"
                }`}
                style={{ maxHeight: compact ? "100px" : "200px" }}
              />
            </div>

            {/* Submit/Stop Button */}
            {isLoading ? (
              <motion.button
                type="button"
                onClick={onStop}
                className={`rounded-lg bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/40 flex items-center justify-center text-rose-400 transition-colors ${
                  compact ? "h-8 w-8" : "h-10 w-10 rounded-xl"
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Stop generation"
              >
                <Square className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
              </motion.button>
            ) : (
              <motion.button
                type="submit"
                disabled={value.trim().length === 0}
                className={`rounded-lg flex items-center justify-center transition-all ${
                  compact ? "h-8 w-8" : "h-10 w-10 rounded-xl"
                } ${getSubmitButtonClass()}`}
                whileHover={value.trim().length > 0 ? { scale: 1.05 } : {}}
                whileTap={value.trim().length > 0 ? { scale: 0.95 } : {}}
                aria-label="Send message"
              >
                <SubmitIcon className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
              </motion.button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default ChatComposer;
